import { useState, useMemo } from "react";
import { usePlatformEvents } from "@acto/taskpane/core/platform/messaging/bridge";
import { useCustomersByCrmId } from "@acto/taskpane/core/data-access/customers/useCustomersByCrmId";
import { useCustomersByDomain } from "@acto/taskpane/core/data-access/customers/useCustomersByDomain";
import { Customer } from "@acto/taskpane/core/domain";

type RecognitionSource = "crm" | "domain" | null;

interface RecognitionState {
  source: RecognitionSource;
  crmId: string | null;
  domain: string | null;
}

export interface RecognizedCustomersResult {
  customers: Customer[];
  isFetching: boolean;
  /** True if recognition resulted in exactly one customer (should auto-navigate) */
  isExactMatch: boolean;
}

export function useRecognizedCustomers(): RecognizedCustomersResult {
  const [state, setState] = useState<RecognitionState>({
    source: null,
    crmId: null,
    domain: null,
  });

  usePlatformEvents({
    "crm.entity": (event) => {
      const email = event.payload.emailAddress;
      const domain = email?.includes("@") ? email.split("@")[1] : null;

      setState({
        source: "crm",
        crmId: event.payload.crmId,
        domain,
      });
    },
    "mail.entity": (event) => {
      const email = event.payload;
      const domain = email?.includes("@") ? email.split("@")[1] : null;

      setState({
        source: "domain",
        crmId: null,
        domain,
      });
    },
  });

  const crmResult = useCustomersByCrmId(state.source === "crm" ? (state.crmId ?? "") : "");
  const domainResult = useCustomersByDomain({ domain: state.domain ?? "" });

  return useMemo(() => {
    if (state.source === "crm") {
      if (crmResult.isFetching) {
        return { customers: [], isFetching: true, isExactMatch: false };
      }

      const crmCustomers = crmResult.data ?? [];

      if (crmCustomers.length > 0) {
        return { customers: crmCustomers, isFetching: false, isExactMatch: crmCustomers.length === 1 };
      }

      if (state.domain) {
        const customers = domainResult.data ?? [];

        return {
          customers,
          isFetching: domainResult.isFetching,
          isExactMatch: customers.length === 1,
        };
      }

      return { customers: [], isFetching: false, isExactMatch: false };
    }

    if (state.source === "domain") {
      const customers = domainResult.data ?? [];
      return {
        customers,
        isFetching: domainResult.isFetching,
        isExactMatch: customers.length === 1,
      };
    }

    return {
      customers: [],
      isFetching: false,
      isExactMatch: false,
    };
  }, [state.source, state.domain, crmResult.data, crmResult.isFetching, domainResult.data, domainResult.isFetching]);
}
